import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, Play, Image as ImageIcon, Film, ChevronDown } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

function isVideoUrl(url) {
  return url && (url.includes('/api/media/') || /\.(mp4|webm|mov|avi|ogg)(\?|$)/i.test(url));
}

/** Thumbnail video con anteprima 2 secondi all'hover — si ferma e torna all'inizio al mouse-leave. */
function VideoThumb({ item, onSelect }) {
  const vidRef = useRef(null);
  const timerRef = useRef(null);

  const startPreview = () => {
    const v = vidRef.current;
    if (!v) return;
    clearTimeout(timerRef.current);
    v.currentTime = 0;
    v.play().catch(() => {});
    timerRef.current = setTimeout(() => {
      if (!vidRef.current) return;
      vidRef.current.pause();
      vidRef.current.currentTime = 0;
    }, 2000);
  };

  const stopPreview = () => {
    clearTimeout(timerRef.current);
    const v = vidRef.current;
    if (v) { v.pause(); v.currentTime = 0; }
  };

  return (
    <div
      className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
      onClick={() => onSelect(item)}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
      data-testid={`gallery-image-${item.id}`}
    >
      <video
        ref={vidRef}
        src={item.image_url}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        muted
        playsInline
        preload="metadata"
      />
      {/* Overlay: icona play scompare mentre il video va */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-all duration-300">
        <div className="w-14 h-14 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:opacity-0 transition-opacity duration-300">
          <Play className="w-6 h-6 text-gray-800 ml-1" />
        </div>
      </div>
      {/* Badge MP4 */}
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded font-body tracking-wide">
        VIDEO
      </div>
    </div>
  );
}

function MediaGrid({ items, onSelect }) {
  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map((item) => {
        const isVid = isVideoUrl(item.image_url);
        if (isVid) return <VideoThumb key={item.id} item={item} onSelect={onSelect} />;
        return (
          <div
            key={item.id}
            className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
            onClick={() => onSelect(item)}
            data-testid={`gallery-image-${item.id}`}
          >
            <img
              src={item.image_url}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          </div>
        );
      })}
    </div>
  );
}

function CollapsibleSection({ icon, title, count, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
      {/* Header / toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-5 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div className="text-left">
            <h2 className="font-heading text-xl font-bold text-text-DEFAULT">{title}</h2>
            <p className="font-body text-sm text-text-muted">{count}</p>
          </div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Animated body */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="p-6 pt-2 bg-white">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export const GalleriaPage = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useState(0);
  const PAGE = 24;
  const { settings } = useSiteSettings();

  const loadPage = async (off = 0, replace = true) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/gallery`, { params: { limit: PAGE, offset: off } });
      setItems(prev => replace ? res.data : [...prev, ...res.data]);
      setHasMore(res.data.length === PAGE);
      offsetRef[1](off + res.data.length);
    } catch (err) { console.error('Error fetching gallery:', err); }
    finally { setLoading(false); setLoadingMore(false); }
  };

  useEffect(() => { loadPage(0, true); }, []); // eslint-disable-line

  const photos = items.filter(i => !isVideoUrl(i.image_url));
  const videos = items.filter(i => isVideoUrl(i.image_url));

  const heroImage = settings.galleria_hero_image_url ||
    'https://images.pexels.com/photos/1084540/pexels-photo-1084540.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  return (
    <div data-testid="galleria-page">
      {/* Hero */}
      <section className="relative flex items-center" data-testid="gallery-hero">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="La nostra galleria"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative container-custom pt-24 pb-6 md:pt-28 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent font-body text-xs uppercase tracking-widest mb-1 block">
              Esplora
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white max-w-3xl">
              Galleria
            </h1>
            <p className="font-body text-sm text-white/80 mt-2 max-w-xl">
              Scopri le immagini e i video della nostra azienda, dei nostri prodotti e del
              magnifico territorio vesuviano.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing" data-testid="gallery-grid">
        <div className="container-custom space-y-6">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                <div key={n} className="animate-pulse aspect-square bg-background-paper rounded-xl" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-body text-text-muted text-lg">Nessun contenuto nella galleria.</p>
            </div>
          ) : (
            <>
              {/* ── Video (primo) ── */}
              {videos.length > 0 && (
                <CollapsibleSection
                  icon={<Film className="w-5 h-5 text-primary" />}
                  title="Video"
                  count={`${videos.length} video`}
                  defaultOpen={true}
                >
                  <MediaGrid items={videos} onSelect={setSelected} />
                </CollapsibleSection>
              )}

              {/* ── Foto (secondo) ── */}
              {photos.length > 0 && (
                <CollapsibleSection
                  icon={<ImageIcon className="w-5 h-5 text-primary" />}
                  title="Foto"
                  count={`${photos.length} immagin${photos.length !== 1 ? 'i' : 'e'}`}
                  defaultOpen={true}
                >
                  <MediaGrid items={photos} onSelect={setSelected} />
                </CollapsibleSection>
              )}
            </>
          )}

          {/* Load More */}
          {!loading && hasMore && (
            <div className="flex justify-center pt-4">
              <button
                onClick={() => loadPage(offsetRef[0], false)}
                disabled={loadingMore}
                className="px-8 py-3 rounded-full bg-primary text-white font-body font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors shadow-md"
              >
                {loadingMore ? 'Caricamento…' : 'Carica altre foto'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="lightbox"
          >
            <button
              onClick={() => setSelected(null)}
              className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors"
              data-testid="lightbox-close"
            >
              <X className="w-8 h-8" />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl max-h-[85vh]"
              onClick={e => e.stopPropagation()}
            >
              {isVideoUrl(selected.image_url) ? (
                <video
                  src={selected.image_url}
                  className="max-w-full max-h-[85vh] rounded-lg"
                  controls
                  autoPlay
                />
              ) : (
                <img
                  src={selected.image_url}
                  alt=""
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
